<template>
    <v-card :loading="loading">
        <v-card-title v-if="!alertMode">
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                PRODUCTION ITEM
            </h2>
        </v-card-title>
        <v-card-title v-else-if="alertMode && createForm">
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                NEW ITEM
            </h2>
        </v-card-title>

        <v-divider v-if="!alertMode" class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mt-2 mb-0">
                <v-row no-gutters v-if="createForm">
                    <v-col cols="12">
                        <v-alert dense outlined type="info" class="mb-4">
                            Maximum {{ MAX_WORK_ORDER_ITEMS }} items per work
                            order.
                        </v-alert>
                    </v-col>
                </v-row>
                <v-row no-gutters v-if="showDisabledProcessesWarning">
                    <v-col cols="12">
                        <v-alert dense outlined type="error" class="mb-4">
                            WARNING: The following processes are disabled and
                            will be removed from the process list:
                            <strong>{{
                                disabledProcessNames.join(', ')
                            }}</strong
                            >.
                        </v-alert>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-combobox
                            v-model="item.partNumber"
                            :items="availablePartNumbers"
                            item-text="code"
                            prepend-icon="mdi-barcode"
                            label="Part number: *"
                            class="mr-xl-2 mr-lg-2 mr-md-2"
                            :rules="partNumberRules"
                            required
                            :disabled="!this.createForm"
                        >
                        </v-combobox>
                    </v-col>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.description"
                            prepend-icon="mdi-text"
                            label="Description: *"
                            :rules="[rules.required]"
                            required
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.mass"
                            type="number"
                            prepend-icon="mdi-weight"
                            label="Mass: (Kgs): *"
                            class="mr-xl-2 mr-lg-2 mr-md-2"
                            :rules="[rules.required, rules.number]"
                            required
                            @input="restrictMassDecimals"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.paintingArea"
                            type="number"
                            prepend-icon="mdi-arrow-expand-all"
                            label="Painting Area: (m2) *"
                            :rules="paintingAreaRules"
                            required
                            @input="restrictPaintingAreaDecimals"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.pieceLength"
                            type="number"
                            prepend-icon="mdi-ruler"
                            label="Piece Length: (mm)"
                            class="mr-xl-2 mr-lg-2 mr-md-2"
                            :rules="optionalDimensionRules"
                            @input="restrictPieceLengthDecimals"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.pieceWidth"
                            type="number"
                            prepend-icon="mdi-ruler-horizontal"
                            label="Piece Width: (mm)"
                            :rules="optionalDimensionRules"
                            @input="restrictPieceWidthDecimals"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-combobox
                            v-model="item.finishAndUCCode"
                            :items="finishes"
                            item-text="name"
                            item-value="id"
                            prepend-icon="mdi-barcode"
                            label="Finish & UC Code: *"
                            class="mr-xl-2 mr-lg-2 mr-md-2"
                            :rules="[rules.required]"
                            required
                        >
                        </v-combobox>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-text-field
                            v-model="item.quantity"
                            type="number"
                            prepend-icon="mdi-database-outline"
                            label="Quantity: *"
                            :rules="[rules.required, rules.number]"
                            required
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-combobox
                            v-model="item.processes"
                            :items="processes"
                            item-text="name"
                            item-value="id"
                            multiple
                            prepend-icon="mdi-table-column"
                            label="Processes: *"
                            :rules="[rules.minItems, rules.required]"
                            required
                            @input="onAlertProcessesInput"
                        >
                        </v-combobox>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-combobox
                            v-model="item.oc"
                            :items="OCs"
                            item-text="name"
                            prepend-icon="mdi-cart-arrow-up"
                            label="OC: "
                        >
                        </v-combobox>
                    </v-col>
                    <v-col cols="12" xl="6" lg="6" md="6">
                        <v-combobox
                            :disabled="!hasComboboxValue(item.oc)"
                            v-model="item.costPosition"
                            :items="availablePositions"
                            item-text="name"
                            prepend-icon=" mdi-numeric"
                            label="Position: "
                            :rules="
                                hasComboboxValue(item.oc)
                                    ? [rules.required]
                                    : []
                            "
                        >
                        </v-combobox>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-text-field
                            v-model="item.notes"
                            prepend-icon="mdi-text"
                            label="Notes: "
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <PartNumberAttachments
                    v-if="alertMode"
                    v-model="itemFiles"
                    :partNumberCode="partNumberCodeForAttachments"
                    :workOrder="workOrder"
                />
            </v-form>
        </v-card-text>
        <v-card-actions v-if="!alertMode || createForm">
            <v-spacer />
            <v-btn
                rounded
                color="primary"
                class="mb-5 mt-n4"
                @click="saveItem"
                :disabled="saveDisabled"
                :loading="loading"
            >
                SAVE
            </v-btn>
        </v-card-actions>
        <v-dialog v-model="showDisabledProcessesConfirmDialog" max-width="520">
            <v-card>
                <v-card-title class="text-h6">Confirm edit</v-card-title>
                <v-card-text>
                    The item includes disabled processes (<strong>{{
                        disabledProcessNames.join(', ')
                    }}</strong
                    >). They were removed from the process list. Do you want to
                    continue saving this edit?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        text
                        @click="showDisabledProcessesConfirmDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        text
                        @click="confirmSaveWithDisabledProcesses"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { objDiff } from '@/helpers/objDiff.js'
import { selectableFinishes, getCurrentFinishId } from '@/helpers/selectableFinishes.js'
import PartNumberAttachments from '@/components/WorkOrders/PartNumberAttachments.vue'
import { MAX_WORK_ORDER_ITEMS } from '@/constants/workOrderLimits'

export default {
    name: 'ItemForm',
    components: {
        PartNumberAttachments,
    },
    props: {
        workOrder: {
            type: Object,
            default: () => ({}),
        },
        partNumbers: {
            type: Array,
            default: () => [],
        },
        item: {
            type: Object,
            default: () => ({}),
        },
        originalItem: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            default: false,
        },
        settingProcesses: {
            type: Object,
            default: () => ({}),
        },
        settingFinish: {
            type: Object,
            default: () => ({}),
        },
        OCs: {
            type: Array,
            default: () => ({}),
        },
        positions: {
            type: Object,
            default: () => ({}),
        },
        alertMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            MAX_WORK_ORDER_ITEMS,
            valid: false,
            loading: false,
            finishes: [],
            processes: [],
            rules: {
                required: v => !!v || 'The value is required',
                number: v => v >= 0 || 'The value must be positive',
                minItems: v => v.length > 0 || 'Select at least one process',
            },
            qualityProcess: {
                id: 'quality',
                name: 'Quality',
                enabled: true,
            },
            readyForPackingProcess: {
                id: 'readyForPacking',
                name: 'Ready for packing',
                enabled: true,
            },
            packingProcess: {
                id: 'packing',
                name: 'Packing',
                enabled: true,
            },
            disabledProcessNames: [],
            showDisabledProcessesConfirmDialog: false,
        }
    },
    computed: {
        itemDiff: function() {
            if (!this.createForm) {
                return objDiff(this.originalItem, this.item)
            } else {
                return null
            }
        },
        isMFFinish() {
            if (!this.item.finishAndUCCode) return false
            const finishName =
                typeof this.item.finishAndUCCode === 'object'
                    ? this.item.finishAndUCCode.name
                    : this.item.finishAndUCCode
            return finishName === 'MF'
        },
        paintingAreaRules() {
            if (this.isMFFinish) {
                return [
                    v => {
                        if (v === null || v === undefined || v === '') {
                            return 'The value is required'
                        }
                        const numValue = Number(v)
                        if (isNaN(numValue)) {
                            return 'Must be a number'
                        }
                        if (numValue < 0) {
                            return 'The value must be positive or zero'
                        }
                        return true
                    },
                ]
            }
            return [this.rules.required, this.rules.number]
        },
        optionalDimensionRules() {
            return [
                v => {
                    if (v === null || v === undefined || v === '') {
                        return true
                    }
                    const numValue = Number(v)
                    if (isNaN(numValue)) {
                        return 'Must be a number'
                    }
                    if (numValue < 0) {
                        return 'The value must be positive or zero'
                    }
                    return true
                },
            ]
        },
        availablePartNumbers() {
            if (!this.createForm) {
                return this.partNumbers
            }
            const usedPartNumberIds = new Set()
            if (this.workOrder.partNumbers) {
                Object.keys(this.workOrder.partNumbers).forEach(
                    partNumberId => {
                        usedPartNumberIds.add(partNumberId)
                    }
                )
            }
            return this.partNumbers.filter(pn => !usedPartNumberIds.has(pn.id))
        },
        availablePositions() {
            if (!this.hasComboboxValue(this.item.oc)) return []
            const ocId =
                typeof this.item.oc === 'object'
                    ? this.item.oc.id
                    : this.item.oc
            return this.positions[ocId] || []
        },
        showDisabledProcessesWarning() {
            return this.disabledProcessNames.length > 0
        },
        saveDisabled() {
            return !this.valid || this.isOcWithoutPosition
        },
        isOcWithoutPosition() {
            return this.hasComboboxValue(this.item.oc) &&
                !this.hasComboboxValue(this.item.costPosition)
        },
        partNumberRules() {
            return [
                this.rules.required,
                value => this.partNumberMustMatchCatalog(value),
            ]
        },
        partNumberCodeForAttachments() {
            const pn = this.item.partNumber
            if (pn == null || pn === '') return ''
            if (typeof pn === 'object') {
                return String(pn.code ?? pn.id ?? '').trim()
            }
            return String(pn).trim()
        },
        itemFiles: {
            get() {
                return this.item.files || []
            },
            set(files) {
                const next = Array.isArray(files) ? [...files] : []
                this.$set(this.item, 'files', next)
                this.$emit('files-changed', next)
            },
        },
    },
    watch: {
        'item.oc'(newValue, oldValue) {
            // Reset position when OC changes
            if (newValue !== oldValue && oldValue !== undefined) {
                this.$nextTick(() => {
                    if (this.item.costPosition) {
                        this.item.costPosition = null
                    }
                })
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            if (this.settingProcesses.id) {
                const fromSetting = this.settingProcesses.processes || []
                const virtualProcesses = [
                    this.qualityProcess,
                    this.readyForPackingProcess,
                    this.packingProcess,
                ]
                const enabledFromSetting = fromSetting.filter(
                    process => process.enabled === true
                )
                const enabledIds = new Set(
                    enabledFromSetting.map(process => process.id)
                )
                this.processes = [
                    ...enabledFromSetting,
                    ...virtualProcesses.filter(v => !enabledIds.has(v.id)),
                ]

                if (this.item.processes?.length > 0) {
                    const selectedIds = this.item.processes.map(p =>
                        typeof p === 'object' && p !== null && 'id' in p
                            ? p.id
                            : p
                    )
                    const byId = new Map(this.processes.map(p => [p.id, p]))
                    const ignoredVirtualIds = new Set([
                        this.qualityProcess.id,
                        this.readyForPackingProcess.id,
                        this.packingProcess.id,
                    ])
                    const disabledOrMissingNames = selectedIds
                        .filter(
                            id => !byId.has(id) && !ignoredVirtualIds.has(id)
                        )
                        .map(id => {
                            const processInSettings = fromSetting.find(
                                p => p.id === id
                            )
                            return processInSettings?.name || id
                        })
                    this.disabledProcessNames = [
                        ...new Set(disabledOrMissingNames),
                    ]
                    this.item.processes = selectedIds
                        .map(id => byId.get(id))
                        .filter(Boolean)
                }
            }
            if (this.settingFinish.id) {
                const currentId = getCurrentFinishId(this.item.finishAndUCCode)
                this.finishes = selectableFinishes(
                    this.settingFinish.finishes,
                    currentId
                )
                if (currentId) {
                    this.item.finishAndUCCode = this.finishes.find(
                        e => e.id == currentId
                    )
                }
            }
            if (this.alertMode && !this.item.files) {
                this.$set(this.item, 'files', [])
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        hasComboboxValue(value) {
            if (value == null || value === '') {
                return false
            }
            if (typeof value === 'object') {
                return !!(value.id || value.name || value.code)
            }
            return String(value).trim().length > 0
        },
        resolveCatalogPartNumber(value) {
            const catalog = this.availablePartNumbers || []
            const matchByCode = text => {
                const normalized = String(text ?? '').trim()
                if (!normalized) return null
                const matches = catalog.filter(
                    pn => String(pn.code ?? '').trim() === normalized
                )
                return matches.length === 1 ? matches[0] : null
            }
            if (value && typeof value === 'object') {
                if (value.id) {
                    const byId = catalog.find(pn => pn.id === value.id)
                    if (byId && String(byId.code ?? '').trim()) return byId
                }
                return matchByCode(value.code)
            }
            return matchByCode(value)
        },
        partNumberMustMatchCatalog(value) {
            if (!this.createForm) return true
            if (value == null || value === '') return true
            return (
                !!this.resolveCatalogPartNumber(value) ||
                'Select a part number from the list'
            )
        },
        toNumericInput(value) {
            if (typeof value === 'number') return value
            if (Array.isArray(value)) return Number(value[0]) || 0
            return Number(value) || 0
        },
        onAlertProcessesInput() {
            if (!this.alertMode || this.createForm) return
            this.$emit('updateProcesses', this.item.processes || [])
        },
        async saveItem() {
            if (
                !this.createForm &&
                this.showDisabledProcessesWarning &&
                !this.showDisabledProcessesConfirmDialog
            ) {
                this.showDisabledProcessesConfirmDialog = true
                return
            }
            await this.executeSaveItem()
        },
        async confirmSaveWithDisabledProcesses() {
            this.showDisabledProcessesConfirmDialog = false
            await this.executeSaveItem()
        },
        async executeSaveItem() {
            if (this.createForm) {
                await this.addNewItem()
            } else {
                await this.updateItem()
            }
        },
        async addNewItem() {
            try {
                this.loading = true
                delete this.item.material
                delete this.item.productionInfo
                delete this.item.processesToShow
                delete this.item.cancelledProduction
                delete this.item.cancelledQuality
                delete this.item.cancelledDeliveries
                const {
                    partNumber,
                    finishAndUCCode,
                    processes,
                    mass,
                    paintingArea,
                    pieceLength,
                    pieceWidth,
                    oc,
                    costPosition,
                    ...paramsToSave
                } = this.item

                const resolvedPartNumber = this.resolveCatalogPartNumber(
                    partNumber
                )
                if (
                    !resolvedPartNumber?.id ||
                    !String(resolvedPartNumber.code || '').trim()
                ) {
                    throw {
                        message: 'Select a part number from the list',
                    }
                }

                if (this.alertMode) {
                    const newItem = {
                        mass: this.toNumericInput(mass),
                        paintingArea: this.toNumericInput(paintingArea),
                        pieceLength: pieceLength
                            ? this.toNumericInput(pieceLength)
                            : undefined,
                        pieceWidth: pieceWidth
                            ? this.toNumericInput(pieceWidth)
                            : undefined,
                        finishAndUCCode: finishAndUCCode.id,
                        settingFinishId: this.settingFinish.id,
                        processes: processes.map(process => process.id),
                        settingProcessesId: this.settingProcesses.id,
                        oc: typeof oc === 'object' ? oc?.name : oc || '',
                        costPosition:
                            typeof costPosition === 'object'
                                ? costPosition?.name
                                : costPosition || '',
                        dataProcesses: processes,
                        dataFinishAndUCCode: finishAndUCCode,
                        ...paramsToSave,
                        partNumberId: resolvedPartNumber.id,
                        partNumber: resolvedPartNumber.code,
                    }
                    this.$emit('addItem', newItem)
                    this.close()
                } else {
                    const newItem = await API.createWorkOrderItem({
                        workOrderId: this.workOrder.id,
                        partNumberId: resolvedPartNumber.id,
                        mass: this.toNumericInput(mass),
                        paintingArea: this.toNumericInput(paintingArea),
                        pieceLength: pieceLength
                            ? this.toNumericInput(pieceLength)
                            : undefined,
                        pieceWidth: pieceWidth
                            ? this.toNumericInput(pieceWidth)
                            : undefined,
                        finishAndUCCode: finishAndUCCode.id,
                        settingFinishId: this.settingFinish.id,
                        processes: processes.map(process => process.id),
                        settingProcessesId: this.settingProcesses.id,
                        oc: typeof oc === 'object' ? oc?.name : oc || '',
                        costPosition:
                            typeof costPosition === 'object'
                                ? costPosition?.name
                                : costPosition || '',
                        ...paramsToSave,
                    })
                    this.$emit('addItem', newItem)
                    this.close()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async updateItem() {
            try {
                this.loading = true

                if (!this.item.oc) {
                    this.item.costPosition = ''
                }
                if (!this.item.oc) {
                    this.item.oc = ''
                }

                const {
                    finishAndUCCode,
                    processes,
                    mass,
                    paintingArea,
                    pieceLength,
                    pieceWidth,
                    oc,
                    costPosition,
                    ...paramsToUpdate
                } = this.itemDiff

                if (this.alertMode) {
                    this.$emit('replaceItem', this.item)
                    this.close()
                } else {
                    const updateItem = await API.updateWorkOrderItem({
                        workOrderId: this.workOrder.id,
                        partNumberId: this.item.partNumberId,
                        ...(finishAndUCCode
                            ? {
                                  finishAndUCCode: finishAndUCCode.id,
                                  settingFinishId: this.settingFinish.id,
                              }
                            : {}),
                        ...(mass
                            ? {
                                  mass: this.toNumericInput(mass),
                              }
                            : {}),
                        ...(paintingArea
                            ? {
                                  paintingArea: this.toNumericInput(
                                      paintingArea
                                  ),
                              }
                            : {}),
                        ...(pieceLength !== undefined
                            ? {
                                  pieceLength: pieceLength
                                      ? this.toNumericInput(pieceLength)
                                      : null,
                              }
                            : {}),
                        ...(pieceWidth !== undefined
                            ? {
                                  pieceWidth: pieceWidth
                                      ? this.toNumericInput(pieceWidth)
                                      : null,
                              }
                            : {}),
                        ...(processes
                            ? {
                                  processes: processes.map(
                                      process => process.id
                                  ),
                                  settingProcessesId: this.settingProcesses.id,
                              }
                            : {}),
                        ...(oc !== undefined
                            ? {
                                  oc:
                                      typeof oc === 'object'
                                          ? oc?.name
                                          : oc || '',
                              }
                            : {}),
                        ...(costPosition !== undefined
                            ? {
                                  costPosition:
                                      typeof costPosition === 'object'
                                          ? costPosition?.name
                                          : costPosition || '',
                              }
                            : {}),
                        ...paramsToUpdate,
                    })
                    this.$emit('replaceItem', updateItem)
                    this.close()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
        restrictMassDecimals() {
            const match = String(this.item.mass ?? '').match(/^\d+\.?\d{0,3}/)
            this.item.mass = match ? match[0] : ''
        },
        restrictPaintingAreaDecimals() {
            const match = String(this.item.paintingArea ?? '').match(
                /^\d+\.?\d{0,3}/
            )
            this.item.paintingArea = match ? match[0] : ''
        },
        restrictPieceLengthDecimals() {
            const match = String(this.item.pieceLength ?? '').match(
                /^\d+\.?\d{0,3}/
            )
            this.item.pieceLength = match ? match[0] : ''
        },
        restrictPieceWidthDecimals() {
            const match = String(this.item.pieceWidth ?? '').match(
                /^\d+\.?\d{0,3}/
            )
            this.item.pieceWidth = match ? match[0] : ''
        },
    },
}
</script>
